import { useState } from 'react'
import { Alert, Button, Dialog, Text, VStack } from '@chakra-ui/react'
import { FiPlay } from 'react-icons/fi'
import { useCanvassThis } from '../../hooks/useCanvassThis'
import { ResponsiveModal } from '../shared/ResponsiveModal'
import { TermsAckPanel } from '../canvasser/TermsAckPanel'
import { extractApiMessage } from '../../lib/apiError'

/**
 * [Canvass this, CT-14] The organizer's entry. Owns the terms modal and the inline blocked / error
 * alerts; `onReady` fires once the self-grant exists so the caller can open the recut modal.
 */
export function CanvassThisButton({ campaignId, onReady }: { campaignId: string; onReady: () => void }) {
  const { start, blockedMessage, isPending } = useCanvassThis(campaignId)
  const [termsOpen, setTermsOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const run = async (acceptTerms: boolean) => {
    setErrorMessage(null)
    try {
      const outcome = await start(acceptTerms)
      if (outcome === 'terms') setTermsOpen(true)
      else if (outcome === 'ready') {
        setTermsOpen(false)
        onReady()
      } else setTermsOpen(false)
    } catch (err) {
      setTermsOpen(false)
      setErrorMessage(extractApiMessage(err, 'Could not start canvassing'))
    }
  }

  return (
    <VStack align="start" gap={2}>
      <Button size="sm" colorPalette="brand" minH="44px" loading={isPending && !termsOpen} onClick={() => void run(false)}>
        <FiPlay />Canvass this
      </Button>
      {blockedMessage && (
        <Alert.Root status="warning" borderRadius="md" py={2}>
          <Alert.Indicator />
          <Text fontSize="sm">{blockedMessage}</Text>
        </Alert.Root>
      )}
      {errorMessage && (
        <Alert.Root status="error" borderRadius="md" py={2}>
          <Alert.Indicator />
          <Text fontSize="sm">{errorMessage}</Text>
        </Alert.Root>
      )}
      <ResponsiveModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} size="md" isCentered>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Body>
              <TermsAckPanel onAccept={() => void run(true)} isSubmitting={isPending} />
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </VStack>
  )
}
